import React from 'react';
import { Button } from '@/src/modules/components/Button';
import { Icons } from '@/src/modules/components/Icons';
import { TopLayerDialog } from './TopLayerDialog';

interface Props {
  open: boolean;
  onClose: () => void;
  closeLabel?: string;
  continueLabel?: string;
  onContinue?: () => void;
  /** Gold for an explicit "use this design". Quiet when a later confirm still has to happen. */
  continueTone?: 'commit' | 'quiet';
  badge: string;
  testId?: string;
  children: React.ReactNode;
}

/**
 * Read-only full-screen preview. Close and an optional continue action only —
 * this shell never publishes.
 */
export function FullScreenPreviewDialog({
  open,
  onClose,
  closeLabel = 'Back',
  continueLabel,
  onContinue,
  continueTone = 'quiet',
  badge,
  testId = 'readonly-preview',
  children,
}: Props) {
  const showContinue = Boolean(continueLabel && onContinue);

  return (
    <TopLayerDialog open={open} onClose={onClose} label={badge} variant="preview" testId={testId}>
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-[#1A202C] px-4 py-3 border-b border-white/10">
        <Button
          autoFocus
          type="button"
          variant="secondary"
          className="bg-black/60 hover:bg-black/80 text-white border-none rounded-full px-5 py-2.5 shadow-lg flex items-center gap-2"
          onClick={onClose}
        >
          <Icons.ArrowLeft size={16} /> {closeLabel}
        </Button>
        {showContinue && (
          <Button
            type="button"
            variant="secondary"
            className={
              continueTone === 'commit'
                ? 'bg-[#D4AF37] hover:bg-[#B8962E] text-[#1A202C] rounded-full px-6 py-2.5 font-bold shadow-lg'
                : 'bg-white/10 hover:bg-white/20 text-white border border-white/25 rounded-full px-6 py-2.5'
            }
            onClick={onContinue}
          >
            {continueLabel}
          </Button>
        )}
        <div className="ml-auto bg-black/50 text-white text-xs font-semibold uppercase tracking-widest rounded-full px-4 py-2 flex items-center gap-2 border border-white/10">
          <Icons.Eye size={13} />
          {badge}
        </div>
      </div>
      <div className="w-full min-h-[100dvh] bg-[#1A202C]">{children}</div>
    </TopLayerDialog>
  );
}
